/**
 * EVERY GOVERNING BODY IN THE OBJECT, AND WHETHER THE BOARD CAN OPEN IT — pure.
 * No React, no I/O.
 *
 * ⚠️ THE ASSERTION IS THE FEATURE. Four buckets, and they must sum to the
 * universe. If they do not, the surface says RECONCILIATION FAILED and names the
 * shortfall rather than printing the buckets as though they were whole — the same
 * discipline queueFor() states in assignmentViews.js, for the same reason: a
 * queue that can silently drop a body is not a queue, and this one exists
 * precisely because a body was silently dropped for months.
 *
 *   onCard        the registry holds an entry, so a card renders
 *   noCard        a real, populated body name with NO registry entry.
 *                 THE PAC-12 SHAPE. Records exist, sometimes a rep owns them,
 *                 and no surface in the product can reach them.
 *   unspecified   `sr_conference` is literally "Unspecified" — not an
 *                 organisation. No registry entry can ever help: the record does
 *                 not say who sanctioned it. This needs DATA, not a card.
 *   blank         `sr_conference` is empty. Same fix, different absence, and
 *                 kept apart from "Unspecified" because one was typed and the
 *                 other never was.
 *
 * ⚠️ AND IT READS `raw.sr_conference`, NOT THE NORMALIZED `conference`.
 * `normalizeChampionship` runs the field through `cleanConference()`, which
 * truncates at the first comma or semicolon. That is right for a label on a card
 * and wrong here: a truncated name is a DIFFERENT string, so it would look up a
 * different registry key and could report a body as cardless because of a comma.
 * tools/audit-map-coverage.mjs reads the raw field, and this has to agree with it.
 */
import { registryCardFor } from '../../lib/bodyRegistryIndex.js'
import { ownerOf, OUT_OF_SCOPE_SEGMENTS } from './assignmentViews.js'

const S = (v) => (typeof v === 'string' ? v.trim() : v == null ? '' : String(v).trim())

/** The value the object uses for "somebody filled this in with nothing". */
export const UNSPECIFIED = 'Unspecified'

/** Cap on the records carried per row, so a 474-row body cannot bloat a render. */
export const SAMPLE_CAP = 300

const top = (m) => {
  let best = '', n = -1
  for (const [k, v] of m) if (v > n) { best = k; n = v }
  return best
}

/**
 * @param records normalized championships (each carrying `.raw`)
 * @returns {{
 *   rows, totals, reconciled, shortfall, noCard, noCardWithRep, noCardNoRep
 * }}
 */
export function bodyCoverage(records) {
  const byBody = new Map()
  let unspecified = 0
  let blank = 0

  for (const c of records || []) {
    const r = c?.raw || c || {}
    const body = S(r.sr_conference)
    if (!body) { blank++; continue }
    if (body === UNSPECIFIED) { unspecified++; continue }
    if (!byBody.has(body)) {
      byBody.set(body, {
        body, count: 0, segments: new Map(), states: new Set(), reps: new Map(),
        crowned: 0, records: [],
      })
    }
    const e = byBody.get(body)
    e.count++
    e.segments.set(S(r.sr_segment) || '(no segment)', (e.segments.get(S(r.sr_segment) || '(no segment)') || 0) + 1)
    const st = S(r.sr_state_location)
    if (st) e.states.add(st)
    const rep = ownerOf(r)
    if (rep) e.reps.set(rep, (e.reps.get(rep) || 0) + 1)
    if (S(r.current_champion)) e.crowned++
    if (e.records.length < SAMPLE_CAP) e.records.push(c)
  }

  const rows = []
  for (const e of byBody.values()) {
    const card = registryCardFor(e.body)
    const segment = top(e.segments)
    rows.push({
      body: e.body,
      count: e.count,
      crowned: e.crowned,
      card,                                   // null == no card
      segment,
      segments: [...e.segments.entries()].sort((a, b) => b[1] - a[1]),
      states: [...e.states].sort(),
      reps: [...e.reps.entries()].sort((a, b) => b[1] - a[1]).map(([rep, n]) => ({ rep, count: n })),
      /* A body whose top segment is somebody else's book needs a BOOK, not a
       * card and not a rep. Saying so is what keeps the queue readable — 3,538
       * records sit under cheer, dance, band, Canada, motorsports, rodeo,
       * equestrian and pro, and labelling them a gap would cry wolf on all of
       * them. See OUT_OF_SCOPE_SEGMENTS. */
      outOfScope: OUT_OF_SCOPE_SEGMENTS.has(segment),
      records: e.records,
      truncated: e.count > e.records.length,
    })
  }
  rows.sort((a, b) => b.count - a.count)

  const onCard = rows.filter((r) => r.card)
  const noCard = rows.filter((r) => !r.card)
  const sum = (a) => a.reduce((n, r) => n + r.count, 0)

  const totals = {
    universe: (records || []).length,
    bodies: rows.length,
    onCardBodies: onCard.length,
    noCardBodies: noCard.length,
    onCard: sum(onCard),
    noCard: sum(noCard),
    unspecified,
    blank,
  }
  totals.accounted = totals.onCard + totals.noCard + totals.unspecified + totals.blank

  const noCardWithRep = noCard.filter((r) => r.reps.length)
  const noCardNoRep = noCard.filter((r) => !r.reps.length)
  totals.noCardWithRep = sum(noCardWithRep)
  totals.noCardNoRep = sum(noCardNoRep)

  return {
    rows,
    onCard,
    noCard,
    noCardWithRep,
    noCardNoRep,
    totals,
    /* The property, not a claim. */
    reconciled: totals.accounted === totals.universe,
    shortfall: totals.universe - totals.accounted,
  }
}
